import React from 'react';
import PropTypes from 'prop-types';

import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';

import Toolbar from '@material-ui/core/Toolbar';

import { reduxForm, FieldArray, Field, SubmissionError } from 'redux-form';

import {
  createTemplate,
  updateTemplate,
} from 'redux/modules/templates';

import Button from 'components/common/Button';
import EditTextField from 'components/fields/EditTextField';
import IconButton from 'components/common/IconButton';
import IconDocument from 'components/icons/IconDocument';

import objectDiff from 'utils/objectDiff';
import * as validate from 'utils/validate';

import TemplatesCreateEditFormBody from './TemplatesCreateEditFormBody';

const styles = () => ({
  gridToolbar: {
    marginBottom: 12,
  },
  typography: {
    marginBottom: 16,
  },
  button: {
    '& + $button': {
      marginLeft: 24,
    },
  },
  iconButton: {
    marginTop: -4,
    verticalAlign: 'top',
    '$button + &': {
      marginLeft: 8,
    },
  },
  iconDocument: {
    width: 16,
    height: 22,
  },
});

function onSubmit(values, dispatch, props) {
  const normalizeValues = (formValues) => {
    const normalizedValues = Object.assign({}, formValues);
    if (normalizedValues.Questions) {
      normalizedValues.Questions = normalizedValues.Questions
        .map(({ Text, Choices, ...otherProps }) => ({
          ...otherProps,
          ...Text && Text.length > 0 ? {
            Text: Text.replace(/[\u{0080}-\u{FFFF}]/gu, ''), // Remove Unicode chars, that are not supported by JSPDF
          } : {},
          ...Choices ? {
            Choices: Choices.map(choice =>
              choice.replace(/[\u{0080}-\u{FFFF}]/gu, '')), // Remove Unicode chars, that are not supported by JSPDF
          } : {},
        }));
    }

    return normalizedValues;
  };
  if (props.variant === 'create') {
    return new Promise((resolve, reject) => {
      dispatch(createTemplate({
        values: normalizeValues(values),
        resolve,
        reject,
      }));
    }).then(res => res,
      (err) => {
        if (err.error) {
          throw new SubmissionError({ _error: err.error });
        }
        throw new SubmissionError(err);
      });
  }
  const diffValues = objectDiff(values, props.initialValues);
  return new Promise((resolve, reject) => {
    dispatch(updateTemplate({
      values: normalizeValues({
        ID: props.initialValues.ID,
        ...diffValues,
        ...diffValues.Questions ? { Questions: values.Questions } : {},
      }),
      resolve,
      reject,
    }));
  }).then(res => res,
    (err) => {
      if (err.error) {
        throw new SubmissionError({ _error: err.error });
      }
      throw new SubmissionError(err);
    });
}

const TemplatesCreateEditForm = ({
  handleSubmit,
  submit,
  pristine,
  submitting,
  variant,
  onPrintPreviewButtonClick,
  initialValues,
  classes,
}) => (
  <form
    noValidate
    onSubmit={handleSubmit}
    autoComplete="off"
  >
    <Toolbar
      className={classes.gridToolbar}
      disableGutters
    >
      <Grid
        container
        spacing={24}
        alignItems="center"
        justify="space-between"
      >
        <Grid item>
          <Field
            name="Name"
            component={EditTextField}
            placeholder="Template Name"
            validate={[validate.required]}
          />
        </Grid>
        <Grid item>
          <Button
            variant="raised"
            className={classes.button}
            href="/settings/templates"
          >
            Cancel
          </Button>
          <Button
            variant="raised"
            color="primary"
            className={classes.button}
            disabled={submitting || (pristine && variant === 'edit') || (pristine && !initialValues.Questions)}
            onClick={submit}
          >
            {variant === 'edit' ? 'Save' : 'Done'}
          </Button>
          <IconButton
            className={classes.iconButton}
            disabled={submitting || (variant === 'create' && pristine
              && (!initialValues || !initialValues.Questions || initialValues.Questions.length === 0
              ))}
            tooltipContent="Print / Preview"
            onClick={onPrintPreviewButtonClick}
          >
            <IconDocument className={classes.iconDocument} />
          </IconButton>
        </Grid>
      </Grid>
    </Toolbar>
    <FieldArray
      name="Questions"
      component={TemplatesCreateEditFormBody}
    />
  </form>
);

TemplatesCreateEditForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  onPrintPreviewButtonClick: PropTypes.func,
  submit: PropTypes.func.isRequired,
  pristine: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  variant: PropTypes.oneOf(['create', 'edit']),
  initialValues: PropTypes.shape({
    Name: PropTypes.string,
  }),
  classes: PropTypes.shape({
    gridToolbar: PropTypes.string.isRequired,
    typography: PropTypes.string.isRequired,
    button: PropTypes.string.isRequired,
    iconButton: PropTypes.string.isRequired,
    iconDocument: PropTypes.string.isRequired,
  }).isRequired,
};

TemplatesCreateEditForm.defaultProps = {
  variant: 'create',
  initialValues: null,
  onPrintPreviewButtonClick: () => {},
};

export default reduxForm({
  onSubmit,
  enableReinitialize: true,
})(withStyles(styles)(TemplatesCreateEditForm));



// WEBPACK FOOTER //
// ./src/components/settings/TemplatesCreateEditForm/TemplatesCreateEditForm.js